import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  Container,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Alert,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatClear,
  TableChart,
  BorderVertical,
  BorderHorizontal,
  DeleteOutline,
  Save,
  Cancel,
  Info as InfoIcon,
  Description as DescriptionIcon,
  AccountTree as RelationsIcon,
  Settings as SettingsIcon,
  Upload as UploadIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { apiClient } from '../../api/apiClient';
import { useAuth } from '../../auth/AuthContext';
import { SACHGEBIETE, KATEGORIEN } from '../../constants/index.js';
import Autocomplete from '@mui/material/Autocomplete';

// Bundesländer aus dem Backend-Schema
const BUNDESLAENDER = {
  BWB: "Baden-Württemberg",
  FSB: "Bayern",
  BLN: "Berlin",
  BRB: "Brandenburg",
  BRE: "Bremen",
  HAM: "Hamburg",
  HES: "Hessen",
  MVP: "Mecklenburg-Vorpommern",
  NSB: "Niedersachsen",
  NRW: "Nordrhein-Westfalen",
  RPF: "Rheinland-Pfalz",
  SAA: "Saarland",
  FSS: "Sachsen",
  SAN: "Sachsen-Anhalt",
  SHS: "Schleswig-Holstein",
  FST: "Thüringen",
  BRD: "Bundesrepublik Deutschland"
};

// Update editor styles
const editorStyles = {
  '.ProseMirror': {
    minHeight: '300px',
    padding: '1rem',
    backgroundColor: '#fff',
    overflowY: 'auto',
    '&:focus': {
      outline: 'none',
    },
    '& table': {
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      width: '100%',
      margin: '0',
      '& td, & th': {
        minWidth: '1em',
        border: '2px solid #ced4da',
        padding: '3px 5px',
        verticalAlign: 'top',
        boxSizing: 'border-box',
        position: 'relative',
        '& > *': {
          margin: 0,
        },
      },
    },
  },
};

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const handleButtonClick = (callback) => (e) => {
    e.preventDefault();
    callback();
  };

  return (
    <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBold().run())}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleItalic().run())}
        >
          <FormatItalic />
        </ToggleButton>
        <ToggleButton
          value="underline"
          selected={editor.isActive('underline')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleUnderline().run())}
        >
          <FormatUnderlined />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBulletList().run())}
        >
          <FormatListBulleted />
        </ToggleButton>
        <ToggleButton
          value="orderedList"
          selected={editor.isActive('orderedList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleOrderedList().run())}
        >
          <FormatListNumbered />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small" exclusive>
        <ToggleButton
          value="left"
          selected={editor.isActive({ textAlign: 'left' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('left').run())}
        >
          <FormatAlignLeft />
        </ToggleButton>
        <ToggleButton
          value="center"
          selected={editor.isActive({ textAlign: 'center' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('center').run())}
        >
          <FormatAlignCenter />
        </ToggleButton>
        <ToggleButton
          value="right"
          selected={editor.isActive({ textAlign: 'right' })}
          onMouseDown={handleButtonClick(() => editor.chain().focus().setTextAlign('right').run())}
        >
          <FormatAlignRight />
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="insertTable"
          onMouseDown={handleButtonClick(() => 
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          )}
        >
          <TableChart />
        </ToggleButton>
        <ToggleButton
          value="addColumnBefore"
          onMouseDown={handleButtonClick(() => editor.chain().focus().addColumnBefore().run())}
          disabled={!editor.can().addColumnBefore()}
        >
          <BorderVertical />
        </ToggleButton>
        <ToggleButton
          value="addRowBefore"
          onMouseDown={handleButtonClick(() => editor.chain().focus().addRowBefore().run())}
          disabled={!editor.can().addRowBefore()}
        >
          <BorderHorizontal />
        </ToggleButton>
        <ToggleButton
          value="deleteTable"
          onMouseDown={handleButtonClick(() => editor.chain().focus().deleteTable().run())}
          disabled={!editor.can().deleteTable()}
        >
          <DeleteOutline />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButton
        value="clear"
        onMouseDown={handleButtonClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}
      >
        <FormatClear />
      </ToggleButton>
    </Box>
  );
};

const SimpleMenuBar = ({ editor }) => {
  if (!editor) return null;

  const handleButtonClick = (callback) => (e) => {
    e.preventDefault();
    callback();
  };

  return (
    <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bold"
          selected={editor.isActive('bold')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBold().run())}
        >
          <FormatBold />
        </ToggleButton>
        <ToggleButton
          value="italic"
          selected={editor.isActive('italic')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleItalic().run())}
        >
          <FormatItalic />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup size="small">
        <ToggleButton
          value="bulletList"
          selected={editor.isActive('bulletList')}
          onMouseDown={handleButtonClick(() => editor.chain().focus().toggleBulletList().run())}
        >
          <FormatListBulleted />
        </ToggleButton>
      </ToggleButtonGroup>

      <ToggleButton
        value="clear"
        onMouseDown={handleButtonClick(() => editor.chain().focus().clearNodes().unsetAllMarks().run())}
      >
        <FormatClear />
      </ToggleButton>
    </Box>
  );
};

const SimpleEditor = ({ content, onChange, minRows = 4 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <Paper 
      sx={{ 
        border: '1px solid rgba(0, 0, 0, 0.23)',
        mb: 2,
      }}
    >
      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <SimpleMenuBar editor={editor} />
      </Box>
      <Box sx={{
        '.ProseMirror': {
          minHeight: `${minRows * 1.5}em`,
          padding: '1rem',
          backgroundColor: '#fff',
          overflowY: 'auto',
          '&:focus': {
            outline: 'none',
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

const SectionTitle = ({ icon: Icon, title }) => (
  <Box sx={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: 1, 
    mb: 2,
    mt: 3,
    color: 'primary.main',
    '&:first-of-type': {
      mt: 0
    }
  }}>
    <Icon />
    <Typography variant="h6">{title}</Typography>
  </Box>
);

// Hilfsfunktionen für XML-Import
const getCategory = (name) => {
  const categories = {
    'kostenbeschluss': 'K',
    'beschluss': 'B',
    'gerichtsbescheid': 'G',
    'teilurteil': 'T',
    'urteil': 'U'
  };
  return categories[name.toLowerCase().trim()] || '';
};

const getSgByCity = (city) => {
  // Mapping von Stadt zu vollständigem Gerichtsnamen
  switch(city) {
    case 'Nürnberg': return 'Sozialgericht Nürnberg';
    case 'München': return 'Sozialgericht München';
    case 'Augsburg': return 'Sozialgericht Augsburg';
    case 'Bayreuth': return 'Sozialgericht Bayreuth';
    case 'Landshut': return 'Sozialgericht Landshut';
    case 'Regensburg': return 'Sozialgericht Regensburg';
    case 'Würzburg': return 'Sozialgericht Würzburg';
    // ... weitere Mappings bleiben gleich
    default: return '';
  }
};

const getLsgByCity = (city) => {
  // Mapping von Stadt zu vollständigem LSG-Namen
  switch(city) {
    case 'München': return 'Bayerisches Landessozialgericht';
    case 'Stuttgart': return 'Landessozialgericht Baden-Württemberg';
    case 'Potsdam': return 'Landessozialgericht Berlin-Brandenburg';
    case 'Hamburg': return 'Landessozialgericht Hamburg';
    case 'Darmstadt': return 'Hessisches Landessozialgericht';
    case 'Neustrelitz': return 'Landessozialgericht Mecklenburg-Vorpommern';
    case 'Celle': return 'Landessozialgericht Niedersachsen-Bremen';
    case 'Essen': return 'Landessozialgericht Nordrhein-Westfalen';
    case 'Mainz': return 'Landessozialgericht Rheinland-Pfalz';
    case 'Saarbrücken': return 'Landessozialgericht für das Saarland';
    case 'Chemnitz': return 'Sächsisches Landessozialgericht';
    case 'Halle': return 'Landessozialgericht Sachsen-Anhalt';
    case 'Schleswig': return 'Schleswig-Holsteinisches Landessozialgericht';
    case 'Erfurt': return 'Thüringer Landessozialgericht';
    default: return '';
  }
};

const getStateByCity = (city) => {
  const stateMapping = {
    'Stendal': 'SAN',
    'Aachen': 'NRW',
    'Altenburg': 'FST',
    'Augsburg': 'FSB',
    'Aurich': 'NSB',
    'Bayreuth': 'FSB',
    'Berlin': 'BRB',
    'Braunschweig': 'NSB',
    'Bremen': 'NSB',
    'Celle': 'NSB',
    'Chemnitz': 'FSS',
    'Cottbus': 'BRB',
    'Darmstadt': 'HES',
    'Dessau-Roßlau': 'SAN',
    'Detmold': 'NRW',
    'Dortmund': 'NRW',
    'Dresden': 'FSS',
    'Duisburg': 'NRW',
    'Düsseldorf': 'NRW',
    'Erfurt': 'FST',
    'Essen': 'NRW',
    'Frankfurt (Oder)': 'BRB',
    'Frankfurt am Main': 'HES',
    'Freiburg': 'BWB',
    'Fulda': 'HES',
    'Gelsenkirchen': 'NRW',
    'Gießen': 'HES',
    'Gotha': 'FST',
    'Halle': 'SAN',
    'Hamburg': 'HAM',
    'Hannover': 'NSB',
    'Heilbronn': 'BWB',
    'Hildesheim': 'NSB',
    'Itzehoe': 'SHS',
    'Karlsruhe': 'BWB',
    'Kassel': 'HES',
    'Kiel': 'SHS',
    'Koblenz': 'RPF',
    'Köln': 'NRW',
    'Konstanz': 'BWB',
    'Landshut': 'FSB',
    'Leipzig': 'FSS',
    'Lübeck': 'SHS',
    'Lüneburg': 'NSB',
    'Magdeburg': 'SAN',
    'Mainz': 'RPF',
    'Mannheim': 'BWB',
    'Marburg': 'HES',
    'Meiningen': 'FST',
    'München': 'FSB',
    'Münster': 'NRW',
    'Neubrandenburg': 'MVP',
    'Neuruppin': 'BRB',
    'Neustrelitz': 'MVP',
    'Nordhausen': 'FST',
    'Nürnberg': 'FSB',
    'Oldenburg': 'NSB',
    'Osnabrück': 'NSB',
    'Potsdam': 'BRB',
    'Regensburg': 'FSB',
    'Reutlingen': 'BWB',
    'Rostock': 'MVP',
    'Saarbrücken': 'SAA',
    'Schleswig': 'SHS',
    'Schwerin': 'MVP',
    'Speyer': 'RPF',
    'Stade': 'NSB',
    'Stralsund': 'MVP',
    'Stuttgart': 'BWB',
    'Trier': 'RPF',
    'Ulm': 'BWB',
    'Wiesbaden': 'HES',
    'Würzburg': 'FSB'
  };
  return stateMapping[city] || '';
};

const parseDate = (input) => {
  const parts = input.match(/(\d+)/g);
  if (parts != null) {
    const date = new Date(parts[2], parts[1]-1, parts[0]);
    const curr_date = date.getDate();
    const curr_month = date.getMonth() + 1;
    const curr_year = date.getFullYear();
    return `${curr_year}-${("0" + curr_month).slice(-2)}-${("0" + curr_date).slice(-2)}`;
  }
  return '';
};

// Neue Vorschau-Komponente
const EntscheidungVorschau = ({ formData, sachgebiete, gerichte, onClose }) => {
  // Bestimme die aktive Instanz basierend auf dem ausgewählten Gericht
  const isFirstInstance = formData.aktenzeichen === formData.aktenzeichen_1_instanz || 
                         (!formData.aktenzeichen_1_instanz && !formData.aktenzeichen_2_instanz && !formData.aktenzeichen_3_instanz);
  const isSecondInstance = formData.aktenzeichen === formData.aktenzeichen_2_instanz;
  const isThirdInstance = formData.aktenzeichen === formData.aktenzeichen_3_instanz;

  return (
    <Box sx={{ 
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    }}>
      {/* Basisinfos */}
      <Paper sx={{ p: 3, mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon color="primary" />
            Basisinfos
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            disabled
          >
            PDF herunterladen
          </Button>
        </Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="textSecondary">
              Aktenzeichen
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 'medium', mt: 0.5 }}>
              {formData.aktenzeichen || '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="textSecondary">
              Gericht
            </Typography>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'primary.main',
              mt: 0.5
            }}>
              <AccountBalanceIcon />
              <Typography variant="h6">
                {formData.gericht_id ? gerichte.find(g => g.id === formData.gericht_id)?.bezeichnung || '-' : '-'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Typography variant="subtitle2" color="textSecondary">
              Kategorie
            </Typography>
            <Chip 
              label={formData.kategorie === 'U' ? 'Urteil' : 
                     formData.kategorie === 'B' ? 'Beschluss' : 
                     formData.kategorie === 'G' ? 'Gerichtsbescheid' : 
                     formData.kategorie || '-'}
              color="primary"
              sx={{ 
                mt: 0.5,
                height: 32,
                '& .MuiChip-label': {
                  fontSize: '1.15rem'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Typography variant="subtitle2" color="textSecondary">
              Sachgebiet
            </Typography>
            <Chip 
              label={sachgebiete[formData.sachgebiet] || '-'}
              color="primary"
              sx={{ 
                mt: 0.5,
                height: 32,
                '& .MuiChip-label': {
                  fontSize: '1.15rem'
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Relationen Kachel */}
      <Paper sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          Beziehungen
        </Typography>
        <Grid container spacing={2}>
          {/* 1. Instanz */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderWidth: isFirstInstance ? 4 : 1,
              borderColor: isFirstInstance ? 'primary.main' : 'divider',
              borderRadius: 1,
              height: '100%',
              boxShadow: isFirstInstance ? 3 : 0,
              bgcolor: isFirstInstance ? 'primary.lighter' : 'transparent'
            }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                1. Instanz
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gericht
                </Typography>
                {formData.gericht_1_instanz_id ? (
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.main'
                  }}>
                    <AccountBalanceIcon fontSize="small" />
                    {gerichte.find(g => g.id === formData.gericht_1_instanz_id)?.bezeichnung || '-'}
                  </Box>
                ) : '-'}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Aktenzeichen
                </Typography>
                <Typography variant="body2">
                  {formData.aktenzeichen_1_instanz || formData.aktenzeichen || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Datum
                </Typography>
                <Typography variant="body2">
                  {formData.datum_1_instanz ? new Date(formData.datum_1_instanz).toLocaleDateString('de-DE') : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* 2. Instanz */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderWidth: isSecondInstance ? 4 : 1,
              borderColor: isSecondInstance ? 'primary.main' : 'divider',
              borderRadius: 1,
              height: '100%',
              boxShadow: isSecondInstance ? 3 : 0,
              bgcolor: isSecondInstance ? 'primary.lighter' : 'transparent',
              opacity: formData.gericht_2_instanz_id ? 1 : 0.5
            }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                2. Instanz
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gericht
                </Typography>
                {formData.gericht_2_instanz_id ? (
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.main'
                  }}>
                    <AccountBalanceIcon fontSize="small" />
                    {gerichte.find(g => g.id === formData.gericht_2_instanz_id)?.bezeichnung || '-'}
                  </Box>
                ) : '-'}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Aktenzeichen
                </Typography>
                <Typography variant="body2">
                  {formData.aktenzeichen_2_instanz || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Datum
                </Typography>
                <Typography variant="body2">
                  {formData.datum_2_instanz ? new Date(formData.datum_2_instanz).toLocaleDateString('de-DE') : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* 3. Instanz */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderWidth: isThirdInstance ? 4 : 1,
              borderColor: isThirdInstance ? 'primary.main' : 'divider',
              borderRadius: 1,
              height: '100%',
              boxShadow: isThirdInstance ? 3 : 0,
              bgcolor: isThirdInstance ? 'primary.lighter' : 'transparent',
              opacity: formData.gericht_3_instanz_id ? 1 : 0.5
            }}>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                3. Instanz
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Gericht
                </Typography>
                {formData.gericht_3_instanz_id ? (
                  <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'primary.main'
                  }}>
                    <AccountBalanceIcon fontSize="small" />
                    {gerichte.find(g => g.id === formData.gericht_3_instanz_id)?.bezeichnung || '-'}
                  </Box>
                ) : '-'}
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Aktenzeichen
                </Typography>
                <Typography variant="body2">
                  {formData.aktenzeichen_3_instanz || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Datum
                </Typography>
                <Typography variant="body2">
                  {formData.datum_3_instanz ? new Date(formData.datum_3_instanz).toLocaleDateString('de-DE') : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bemerkung */}
      {formData.bemerkung && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" color="textSecondary">
            Bemerkung
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: formData.bemerkung }}
          />
        </Paper>
      )}

      {/* Leitsätze */}
      {formData.leitsaetze && formData.leitsaetze.trim() !== '' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Leitsätze
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: formData.leitsaetze }} 
          />
        </Paper>
      )}

      {/* Deskriptoren */}
      {formData.deskriptoren && formData.deskriptoren.trim() !== '' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Deskriptoren
          </Typography>
          <Typography
            component="div"
            dangerouslySetInnerHTML={{ __html: formData.deskriptoren }}
          />
        </Paper>
      )}

      {/* Normenkette */}
      {formData.normenkette && formData.normenkette.trim() !== '' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Normenkette
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: formData.normenkette }} 
          />
        </Paper>
      )}

      {/* Entscheidungstext */}
      {formData.text && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Entscheidungstext
          </Typography>
          <Typography 
            variant="body1" 
            component="div"
            sx={{
              '& p': { marginBottom: 2 },
              '& br': { marginBottom: 1 }
            }}
            dangerouslySetInnerHTML={{ __html: formData.text }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default function EntscheidungAdd() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [gerichte, setGerichte] = useState([]);
  const [alleGerichte, setAlleGerichte] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sachgebiete, setSachgebiete] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    aktenzeichen: '',
    aktenzeichen_1_instanz: '',
    aktenzeichen_2_instanz: '',
    aktenzeichen_3_instanz: '',
    datum_1_instanz: '',
    datum_2_instanz: '',
    datum_3_instanz: '',
    sachgebiet: '',
    text: '',
    gericht_id: '',
    gericht_1_instanz_id: '',
    gericht_2_instanz_id: '',
    gericht_3_instanz_id: '',
    kategorie: '',
    leitsaetze: '',
    deskriptoren: '',
    normenkette: '',
    abteilung: '',
    land: '',
    bemerkung: '',
    active: true
  });
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, Underline, TextAlign, Table, TableRow, TableHeader, TableCell],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        text: editor.getHTML()
      }));
    },
  });

  const leitsaetzeEditor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        leitsaetze: editor.getHTML()
      }));
    },
  });

  const deskriptorenEditor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        deskriptoren: editor.getHTML()
      }));
    },
  });

  const normenketteEditor = useEditor({
    extensions: [StarterKit, Underline],
    content: '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        normenkette: editor.getHTML()
      }));
    },
  });

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [gerichteResponse, alleGerichteResponse, rolesResponse] = await Promise.all([
          apiClient.get('/gerichte/?assigned=true', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          apiClient.get('/gerichte/', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }),
          apiClient.get('/roles/', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          })
        ]);

        console.log('SACHGEBIETE:', SACHGEBIETE);
        console.log('KATEGORIEN:', KATEGORIEN);
        console.log('Gerichte:', gerichteResponse.data);
        console.log('Alle Gerichte:', alleGerichteResponse.data);
        console.log('Roles:', rolesResponse.data);

        setRoles(rolesResponse.data);
        setGerichte(gerichteResponse.data);
        setAlleGerichte(alleGerichteResponse.data);
        setSachgebiete(SACHGEBIETE || []);
        setKategorien(KATEGORIEN || []);

      } catch (error) {
        console.error('Fehler bei der Initialisierung:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [userData]);

  useEffect(() => {
    if (userData?.role_id && roles.length > 0) {
      const role = roles.find(r => r.id === userData.role_id);
      if (!role?.permissions?.some(p => p.name === 'admin') && userData?.gericht_id) {
        setFormData(prev => ({
          ...prev,
          gericht_id: userData.gericht_id
        }));
      }
    }
  }, [userData, roles]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    
    try {
      // Erstelle eine Kopie der FormData und entferne leere Gerichts-IDs
      const requestData = { ...formData };
      if (!requestData.gericht_1_instanz_id) delete requestData.gericht_1_instanz_id;
      if (!requestData.gericht_2_instanz_id) delete requestData.gericht_2_instanz_id;
      if (!requestData.gericht_3_instanz_id) delete requestData.gericht_3_instanz_id;

      // Konvertiere leere Datumswerte zu null
      if (!requestData.datum_1_instanz) requestData.datum_1_instanz = null;
      if (!requestData.datum_2_instanz) requestData.datum_2_instanz = null;
      if (!requestData.datum_3_instanz) requestData.datum_3_instanz = null;

      const response = await apiClient.post('/entscheidungen/', requestData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Extrahiere die ID aus der Response und leite zur Edit-Seite weiter
      const entscheidungId = response.data.id;
      navigate(`/admin/entscheidungen/edit/${entscheidungId}`);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      
      // Detaillierte Fehlerbehandlung
      let errorMessage = "Ein unbekannter Fehler ist aufgetreten";
      let errorDetails = [];

      if (error.response) {
        // Server hat geantwortet, aber mit einem Fehlercode
        const { status, data } = error.response;
        
        if (data?.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            // ValidationError mit mehreren Fehlern
            errorMessage = "Validierungsfehler";
            errorDetails = data.detail.map(err => {
              if (typeof err === 'object' && err.msg && err.loc) {
                return `${err.loc.join('.')}: ${err.msg}`;
              }
              return typeof err === 'string' ? err : JSON.stringify(err);
            });
          } else if (typeof data.detail === 'object') {
            errorMessage = data.detail.message || "Fehler beim Speichern";
            if (data.detail.errors) {
              errorDetails = Array.isArray(data.detail.errors) 
                ? data.detail.errors 
                : [data.detail.errors];
            }
          }
        } else {
          // Fallback für verschiedene HTTP-Status-Codes
          switch (status) {
            case 400:
              errorMessage = "Ungültige Daten übermittelt";
              break;
            case 401:
              errorMessage = "Nicht autorisiert - bitte melden Sie sich erneut an";
              break;
            case 403:
              errorMessage = "Keine Berechtigung für diese Aktion";
              break;
            case 422:
              errorMessage = "Validierungsfehler bei den übermittelten Daten";
              break;
            case 500:
              errorMessage = "Interner Serverfehler - bitte versuchen Sie es später erneut";
              break;
            default:
              errorMessage = `Fehler ${status}: ${data?.message || 'Unbekannter Serverfehler'}`;
          }
        }
      } else if (error.request) {
        // Request wurde gesendet, aber keine Antwort erhalten
        errorMessage = "Keine Verbindung zum Server - bitte prüfen Sie Ihre Internetverbindung";
      } else {
        // Fehler beim Erstellen des Requests
        errorMessage = error.message || "Fehler beim Senden der Anfrage";
      }

      setSaveError({
        message: errorMessage,
        details: errorDetails
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/entscheidungen');
  };

  const isAdmin = roles.find(r => r.id === userData?.role_id)?.permissions?.some(p => p.name === 'admin');

  const handleEditorChange = (field) => (content) => {
    setFormData(prev => ({
      ...prev,
      [field]: content
    }));
  };

  const handleXMLUpload = (event) => {
    console.log('XML Upload gestartet...');
    setSaveError(null);
    const file = event.target.files[0];
    console.log('Datei:', file);
    
    if (!file) {
      console.error('Keine Datei ausgewählt');
      return;
    }

    if (file.size === 0) {
      console.error('Die Datei ist leer');
      return;
    }

    // Prüfe den MIME-Type und die Dateiendung
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'xml') {
      console.error('Keine XML-Datei (falsche Dateiendung)');
      return;
    }

    console.log('Starte Datei-Lesen...');
    const reader = new FileReader();
    
    reader.onerror = (error) => {
      console.error('Fehler beim Lesen der Datei:', error);
    };

    reader.onload = (e) => {
      console.log('Datei erfolgreich gelesen');
      console.log('Dateiinhalt:', e.target.result);
      
      if (!e.target.result) {
        console.error('Gelesener Dateiinhalt ist leer');
        return;
      }

      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(e.target.result, 'text/xml');
        
        // Prüfe auf Parse-Fehler
        const parseError = xmlDoc.getElementsByTagName('parsererror');
        if (parseError.length > 0) {
          console.error('XML Parse Fehler:', parseError[0].textContent);
          return;
        }

        // Validiere die Grundstruktur der XML
        const rootNode = xmlDoc.documentElement;
        if (!rootNode || rootNode.nodeName !== 'gerichtdb') {
          console.error('Ungültige XML-Struktur: Root-Element "gerichtdb" nicht gefunden');
          return;
        }

        console.log('XML erfolgreich geparst:', xmlDoc);
        console.log('XML Root Element:', rootNode.nodeName);

        // Hilfsfunktion zum Extrahieren von XML-Werten
        const getXMLValue = (xpath) => {
          try {
            const result = xmlDoc.evaluate(xpath, xmlDoc, null, XPathResult.STRING_TYPE, null).stringValue;
            console.log(`XPath ${xpath}:`, result);
            return result;
          } catch (error) {
            console.error(`Fehler bei XPath ${xpath}:`, error);
            return '';
          }
        };

        try {
          // Extrahiere Normenkette
          console.log('Extrahiere Normenkette...');
          const normenSnapshot = xmlDoc.evaluate('/gerichtdb/tabnormen/normen', xmlDoc, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
          console.log('Gefundene Normen:', normenSnapshot.snapshotLength);
          let normenkette = '';
          for (let i = 0; i < normenSnapshot.snapshotLength; i++) {
            const normenChildren = normenSnapshot.snapshotItem(i).children;
            const normData = Array.from(normenChildren).map(child => child.textContent);
            console.log('Norm:', normData);
            normenkette += `${normData[0]} ${normData[1]}<br>`;
          }

          // Extrahiere Deskriptoren
          console.log('Extrahiere Deskriptoren...');
          const deskriptorenSnapshot = xmlDoc.evaluate('/gerichtdb/tabhauptschlagwoerter/hauptschlagwoerter', xmlDoc, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
          console.log('Gefundene Deskriptoren:', deskriptorenSnapshot.snapshotLength);
          let deskriptoren = '';
          for (let i = 0; i < deskriptorenSnapshot.snapshotLength; i++) {
            const deskriptorenChildren = deskriptorenSnapshot.snapshotItem(i).children;
            const deskriptorenData = Array.from(deskriptorenChildren).map(child => child.textContent);
            console.log('Deskriptor:', deskriptorenData);
            deskriptoren += `${deskriptorenData[0]}<br>`;
          }

          // Extrahiere Leitsätze
          console.log('Extrahiere Leitsätze...');
          let leitsaetze = getXMLValue('/gerichtdb/tabkurztexte/leitsatz');
          leitsaetze = leitsaetze.replace(/(\r\n|\r|\n)(\d\. )/g, "$1<br>$2");
          console.log('Formatierte Leitsätze:', leitsaetze);

          // Aktualisiere Formular basierend auf Gerichtstyp
          const newFormData = { ...formData };
          
          // Gemeinsame Daten für alle Gerichtstypen
          newFormData.aktenzeichen = getXMLValue('/gerichtdb/metadaten/az1');
          
          // Hole Gerichtstyp und -ort aus den Metadaten
          const gertyp = getXMLValue('/gerichtdb/metadaten/gertyp');
          const gerort = getXMLValue('/gerichtdb/metadaten/gerort');
          console.log('Gerichtstyp:', gertyp);
          console.log('Gerichtsort:', gerort);
          
          if (gertyp === 'SG') {
            console.log('[XML-Import] 1. Instanz (SG):');
            newFormData.aktenzeichen_1_instanz = getXMLValue('/gerichtdb/metadaten/az1');
            newFormData.datum_1_instanz = parseDate(getXMLValue('/gerichtdb/metadaten/entscheidungsdatum'));
            const sgOrt = getXMLValue('/gerichtdb/metadaten/gerort');
            const sgCode = getSgByCity(sgOrt);
            console.log('  Ausgelesener Ort:', sgOrt);
            console.log('  Erwarteter Gerichtsname:', sgCode);
            console.log('  Gerichte zur Auswahl:', alleGerichte.map(g => ({ id: g.id, bezeichnung: g.bezeichnung })));
            alleGerichte.forEach(g => {
              const match = g.bezeichnung === sgCode;
              console.log(`  Vergleiche "${g.bezeichnung}" (ID: ${g.id}) mit "${sgCode}" => ${match ? 'MATCH' : 'NO MATCH'}`);
            });
            const matchingGericht = alleGerichte.find(g => g.bezeichnung === sgCode);
            if (matchingGericht) {
              newFormData.gericht_id = matchingGericht.id;
              newFormData.gericht_1_instanz_id = matchingGericht.id;
              console.log('  MATCH gefunden:', matchingGericht);
            } else {
              console.warn('  Kein passendes Gericht gefunden für:', sgCode);
            }
            newFormData.land = getStateByCity(sgOrt);
          } else if (gertyp === 'LSG') {
            console.log('[XML-Import] 1. Instanz (LSG):');
            newFormData.aktenzeichen_1_instanz = getXMLValue('/gerichtdb/tabrzug/rzug/rzugaktenzeichen');
            newFormData.datum_1_instanz = parseDate(getXMLValue('/gerichtdb/tabrzug/rzug/rzugdatum'));
            const rzuggerort = getXMLValue('/gerichtdb/tabrzug/rzug/rzuggerort');
            const sgCode = getSgByCity(rzuggerort);
            console.log('  Ausgelesener Ort:', rzuggerort);
            console.log('  Erwarteter Gerichtsname:', sgCode);
            console.log('  Gerichte zur Auswahl:', alleGerichte.map(g => ({ id: g.id, bezeichnung: g.bezeichnung })));
            alleGerichte.forEach(g => {
              const match = g.bezeichnung === sgCode;
              console.log(`  Vergleiche "${g.bezeichnung}" (ID: ${g.id}) mit "${sgCode}" => ${match ? 'MATCH' : 'NO MATCH'}`);
            });
            const matchingGericht = alleGerichte.find(g => g.bezeichnung === sgCode);
            if (matchingGericht) {
              newFormData.gericht_1_instanz = matchingGericht.id;
              newFormData.gericht_1_instanz_id = matchingGericht.id;
              console.log('  MATCH gefunden:', matchingGericht);
            } else {
              console.warn('  Kein passendes Gericht gefunden für:', sgCode);
            }
            // LSG Daten
            newFormData.aktenzeichen_2_instanz = getXMLValue('/gerichtdb/metadaten/az1');
            newFormData.datum_2_instanz = parseDate(getXMLValue('/gerichtdb/metadaten/entscheidungsdatum'));
            const lsgCode = getLsgByCity(getXMLValue('/gerichtdb/metadaten/gerort'));
            console.log('[XML-Import] 2. Instanz (LSG):');
            console.log('  Ausgelesener Ort:', getXMLValue('/gerichtdb/metadaten/gerort'));
            console.log('  Erwarteter Gerichtsname:', lsgCode);
            alleGerichte.forEach(g => {
              const normalizedBezeichnung = g.bezeichnung.toLowerCase().replace(/[^a-z0-9]/g, '');
              const normalizedLsgCode = lsgCode.toLowerCase().replace(/[^a-z0-9]/g, '');
              const match = normalizedBezeichnung === normalizedLsgCode;
              console.log(`  Vergleiche (normalisiert) "${normalizedBezeichnung}" (ID: ${g.id}) mit "${normalizedLsgCode}" => ${match ? 'MATCH' : 'NO MATCH'}`);
            });
            const matchingLSG = alleGerichte.find(g => {
              const normalizedBezeichnung = g.bezeichnung.toLowerCase().replace(/[^a-z0-9]/g, '');
              const normalizedLsgCode = lsgCode.toLowerCase().replace(/[^a-z0-9]/g, '');
              return normalizedBezeichnung === normalizedLsgCode;
            });
            if (matchingLSG) {
              newFormData.gericht_2_instanz = matchingLSG.id;
              newFormData.gericht_2_instanz_id = matchingLSG.id;
              newFormData.gericht_id = matchingLSG.id;
              console.log('  MATCH gefunden (2. Instanz):', matchingLSG);
            } else {
              console.warn('  Kein passendes LSG gefunden für:', lsgCode);
            }
            newFormData.land = getStateByCity(getXMLValue('/gerichtdb/metadaten/gerort'));
          }

          // Gemeinsame Daten
          newFormData.kategorie = getCategory(getXMLValue('/gerichtdb/metadaten/entscheidungsart'));
          newFormData.sachgebiet = getXMLValue('/gerichtdb/metadaten/az1').match(/(?<=[A-Z] \d* )(.*?)(?= )/g)?.[0] || '';
          newFormData.normenkette = normenkette;
          newFormData.deskriptoren = deskriptoren;
          newFormData.leitsaetze = leitsaetze;

          console.log('Neues Formular-Daten:', newFormData);

          // Update Form
          setFormData(newFormData);
          
          // Update TipTap Editors
          if (editor) {
            console.log('Aktualisiere Haupteditor...');
            editor.commands.setContent(newFormData.text || '');
          }
          if (leitsaetzeEditor) {
            console.log('Aktualisiere Leitsätze-Editor...');
            leitsaetzeEditor.commands.setContent(newFormData.leitsaetze || '');
          }
          if (deskriptorenEditor) {
            console.log('Aktualisiere Deskriptoren-Editor...');
            deskriptorenEditor.commands.setContent(newFormData.deskriptoren || '');
          }
          if (normenketteEditor) {
            console.log('Aktualisiere Normenkette-Editor...');
            normenketteEditor.commands.setContent(newFormData.normenkette || '');
          }

          console.log('XML-Import abgeschlossen');
        } catch (error) {
          console.error('Fehler beim Verarbeiten der XML:', error);
        }
      } catch (error) {
        console.error('Fehler beim Verarbeiten der XML:', error);
      }
    };

    reader.readAsText(file);
  };

  // Alphabetisch sortierte Gerichte
  const sortedGerichte = [...gerichte].sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));
  const sortedAlleGerichte = [...alleGerichte].sort((a, b) => a.bezeichnung.localeCompare(b.bezeichnung));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Neue Entscheidung</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                disabled={saving}
              >
                XML Import
                <input
                  type="file"
                  accept=".xml"
                  hidden
                  onChange={handleXMLUpload}
                />
              </Button>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setShowPreview(true)}
                disabled={saving}
              >
                Vorschau
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={saving}
              >
                Abbrechen
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </Box>
          </Box>

          {/* Fehleranzeige */}
          {saveError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {saveError.message}
              </Typography>
              {saveError.details && saveError.details.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">Details:</Typography>
                  <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                    {saveError.details.map((detail, index) => (
                      <li key={index}>
                        <Typography variant="body2">{detail}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(event) => {
                    setFormData(prev => ({
                      ...prev,
                      active: event.target.checked
                    }));
                  }}
                  color="primary"
                  disabled={saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">
                    {formData.active ? "Veröffentlicht" : "Nicht veröffentlicht"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formData.active 
                      ? "Die Entscheidung ist öffentlich sichtbar" 
                      : "Die Entscheidung ist nur intern sichtbar"}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <form>
            <Grid container spacing={3}>
              {/* Basisinfos */}
              <Grid item xs={12}>
                <SectionTitle icon={InfoIcon} title="Basisinfos" />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen"
                      label="Aktenzeichen"
                      value={formData.aktenzeichen}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedGerichte.find(g => g.id === formData.gericht_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht" fullWidth />
                      )}
                      disabled={!isAdmin && userData?.gericht_id}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Kategorie</InputLabel>
                      <Select
                        name="kategorie"
                        value={formData.kategorie}
                        onChange={handleInputChange}
                        label="Kategorie"
                      >
                        <MenuItem value="">
                          <em>Bitte wählen</em>
                        </MenuItem>
                        {Array.isArray(kategorien) && kategorien.map((kat) => (
                          <MenuItem key={kat.code} value={kat.code}>
                            {kat.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Sachgebiet</InputLabel>
                      <Select
                        name="sachgebiet"
                        value={formData.sachgebiet}
                        onChange={handleInputChange}
                        label="Sachgebiet"
                      >
                        <MenuItem value="">
                          <em>Bitte wählen</em>
                        </MenuItem>
                        {Array.isArray(sachgebiete) && sachgebiete.map((sg) => (
                          <MenuItem key={sg.code} value={sg.code}>
                            {sg.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <TextField
                        name="abteilung"
                        label="Abteilung"
                        value={formData.abteilung}
                        onChange={handleInputChange}
                        fullWidth
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Land</InputLabel>
                      <Select
                        name="land"
                        value={formData.land}
                        onChange={handleInputChange}
                        label="Land"
                      >
                        <MenuItem value="">
                          <em>Bitte wählen</em>
                        </MenuItem>
                        {BUNDESLAENDER && Object.entries(BUNDESLAENDER).map(([code, name]) => (
                          <MenuItem key={code} value={code}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Relationen */}
              <Grid item xs={12}>
                <SectionTitle icon={RelationsIcon} title="Relationen" />
                <Grid container spacing={3}>
                  {/* Erste Instanz */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen_1_instanz"
                      label="Aktenzeichen (1. Instanz)"
                      value={formData.aktenzeichen_1_instanz || ''}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="datum_1_instanz"
                      label="Datum (1. Instanz)"
                      type="date"
                      value={formData.datum_1_instanz}
                      onChange={handleInputChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht 1. Instanz Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedAlleGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedAlleGerichte.find(g => g.id === formData.gericht_1_instanz_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_1_instanz_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht 1. Instanz" fullWidth />
                      )}
                    />
                  </Grid>

                  {/* Zweite Instanz */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen_2_instanz"
                      label="Aktenzeichen (2. Instanz)"
                      value={formData.aktenzeichen_2_instanz}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="datum_2_instanz"
                      label="Datum (2. Instanz)"
                      type="date"
                      value={formData.datum_2_instanz}
                      onChange={handleInputChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht 2. Instanz Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedAlleGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedAlleGerichte.find(g => g.id === formData.gericht_2_instanz_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_2_instanz_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht 2. Instanz" fullWidth />
                      )}
                    />
                  </Grid>

                  {/* Dritte Instanz */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="aktenzeichen_3_instanz"
                      label="Aktenzeichen (3. Instanz)"
                      value={formData.aktenzeichen_3_instanz}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      name="datum_3_instanz"
                      label="Datum (3. Instanz)"
                      type="date"
                      value={formData.datum_3_instanz}
                      onChange={handleInputChange}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {/* Gericht 3. Instanz Auswahl mit Autocomplete */}
                    <Autocomplete
                      options={sortedAlleGerichte}
                      getOptionLabel={option => option.bezeichnung || ''}
                      value={sortedAlleGerichte.find(g => g.id === formData.gericht_3_instanz_id) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          gericht_3_instanz_id: newValue ? newValue.id : ''
                        }));
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => (
                        <TextField {...params} label="Gericht 3. Instanz" fullWidth />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Entscheidungstext */}
              <Grid item xs={12}>
                <SectionTitle icon={DescriptionIcon} title="Entscheidungstext" />
                <Paper 
                  sx={{ 
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    mb: 2,
                  }}
                >
                  <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                    <MenuBar editor={editor} />
                  </Box>
                  <Box sx={editorStyles}>
                    <EditorContent editor={editor} />
                  </Box>
                </Paper>
              </Grid>

              {/* Zusätzliche Informationen */}
              <Grid item xs={12}>
                <SectionTitle icon={SettingsIcon} title="Zusätzliche Informationen" />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Leitsätze</Typography>
                    <Paper sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', mb: 2 }}>
                      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                        <SimpleMenuBar editor={leitsaetzeEditor} />
                      </Box>
                      <Box sx={editorStyles}>
                        <EditorContent editor={leitsaetzeEditor} />
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Deskriptoren</Typography>
                    <Paper sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', mb: 2 }}>
                      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                        <SimpleMenuBar editor={deskriptorenEditor} />
                      </Box>
                      <Box sx={editorStyles}>
                        <EditorContent editor={deskriptorenEditor} />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Normenkette</Typography>
                    <Paper sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', mb: 2 }}>
                      <Box sx={{ p: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
                        <SimpleMenuBar editor={normenketteEditor} />
                      </Box>
                      <Box sx={editorStyles}>
                        <EditorContent editor={normenketteEditor} />
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>Bemerkung</Typography>
                    <SimpleEditor
                      content={formData.bemerkung}
                      onChange={handleEditorChange('bemerkung')}
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>

          {/* Vorschau Dialog */}
          <Dialog
            open={showPreview}
            onClose={() => setShowPreview(false)}
            maxWidth="xl"
            fullWidth
            PaperProps={{
              sx: {
                maxHeight: '90vh',
                height: '90vh'
              }
            }}
          >
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pb: 1
            }}>
              Vorschau der Entscheidung
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setShowPreview(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <EntscheidungVorschau 
                formData={formData}
                sachgebiete={sachgebiete.reduce((acc, sg) => {
                  acc[sg.code] = sg.name;
                  return acc;
                }, {})}
                gerichte={gerichte}
                onClose={() => setShowPreview(false)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPreview(false)}>Schließen</Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Box>
    </Container>
  );
} 